import React, { useRef, useState } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { GlowCard } from "../common/spotlight-card";
import { ShinyButton } from "../common/shiny-button";

import ServicesModal from "../modals/ServiceModal";
import QuickContactModal from "../modals/QuickContactModal";

import PORTRAIT from "../../assets/images/portrait-1.webp";

export interface ProfileCardProps {
  name?: string;
  title?: string;
  description?: string;
  imageUrl?: string | any;
  className?: string;
}

export function ProfileCard(props: ProfileCardProps) {
  const {
    name = "Patrick",
    title = "Multi-Disciplinary Freelancer",
    description = "I architect scalable Full-Stack Web & Mobile applications, and craft engaging Video content",
    imageUrl = PORTRAIT,
    className,
  } = props;

  const cardRef = useRef<HTMLDivElement>(null);

  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isQuickContactModalOpen, setIsQuickContactModalOpen] = useState(false);

  useGSAP(
    () => {
      gsap.fromTo(
        ".profile-animate",
        { opacity: 0, x: 30 },
        { opacity: 1, x: 0, duration: 3, ease: "power3.inOut", stagger: 0.2 },
      );

      gsap.fromTo(
        ".social-icon-animate",
        { opacity: 0, scale: 0, y: 15 },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 0.4,
          ease: "back.out(1.7)",
          stagger: 0.1,
          delay: 1.5,
        },
      );
    },
    { scope: cardRef },
  );

  return (
    <>
      <div
        ref={cardRef}
        className={`w-full max-w-5xl mx-auto ${className || ""}`}
      >
        {/* Desktop */}
        <div className="hidden md:flex relative items-center justify-center">
          <div className="hover-3d">
            <div className="profile-animate w-[470px] h-[470px] rounded-3xl overflow-hidden flex-shrink-0 flex items-center justify-center z-10 shadow-xl">
              <img
                src={imageUrl}
                alt={name}
                className="w-full h-full object-cover"
                draggable={false}
              />
            </div>
          </div>

          <div className="profile-animate z-20 max-w-xl flex-1 ml-[-80px]">
            <GlowCard
              customSize={true}
              glowColor="blue"
              className="!bg-base-100/80 shadow-2xl rounded p-10 border-none"
            >
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-white mb-2 text-left">
                  {name}
                </h2>
                <p className="text-sm font-medium text-slate-400 text-left">
                  {title}
                </p>
              </div>

              <p className="text-gray-300 text-base leading-relaxed mb-8 text-left">
                {description}
              </p>

              <div className="flex space-x-4">
                <ShinyButton
                  variant="outline"
                  onClick={() => setIsServiceModalOpen(true)}
                  className="social-icon-animate"
                >
                  Start a Project
                </ShinyButton>

                <ShinyButton
                  variant="outline"
                  onClick={() => setIsQuickContactModalOpen(true)}
                  className="social-icon-animate"
                >
                  Quick Connect
                </ShinyButton>
              </div>
            </GlowCard>
          </div>
        </div>

        {/* Mobile */}
        <div className="profile-animate md:hidden max-w-sm mx-auto text-center mt-20">
          <GlowCard
            customSize={true}
            glowColor="blue"
            className="!bg-base-100/80 p-6 rounded border-none"
          >
            <div className="w-full aspect-square bg-base-200 rounded-2xl overflow-hidden mb-6 flex items-center justify-center">
              <img
                src={imageUrl}
                alt={name}
                className="w-full h-full object-cover"
                draggable={false}
              />
            </div>

            <div className="px-2">
              <h2 className="text-xl font-bold text-base-content mb-2">
                {name}
              </h2>
              <p className="text-sm font-medium text-base-content/70 mb-4">
                {title}
              </p>
              <p className="text-base-content text-sm leading-relaxed mb-6">
                {description}
              </p>

              <div className="flex flex-col space-y-3 items-center">
                <ShinyButton
                  variant="outline"
                  onClick={() => setIsServiceModalOpen(true)}
                  className="social-icon-animate w-full"
                >
                  Start a Project
                </ShinyButton>

                <ShinyButton
                  variant="outline"
                  onClick={() => setIsQuickContactModalOpen(true)}
                  className="social-icon-animate w-full"
                >
                  Quick Connect
                </ShinyButton>
              </div>
            </div>
          </GlowCard>
        </div>
      </div>

      <ServicesModal
        isOpen={isServiceModalOpen}
        onClose={() => setIsServiceModalOpen(false)}
      />
      <QuickContactModal
        isOpen={isQuickContactModalOpen}
        onClose={() => setIsQuickContactModalOpen(false)}
      />
    </>
  );
}
